import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, formatApiError } from '../../lib/api'
import { MarketingShell } from '../../components/MarketingShell'
import { Seo } from '../../components/Seo'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'

type AccountingSystem = 'Xero' | 'QuickBooks' | 'Sage' | 'Other'

const SYSTEMS: AccountingSystem[] = ['Xero', 'QuickBooks', 'Sage', 'Other']

const nextSteps = [
  { when: 'Within a day', body: 'a reply from a person, with a link to connect your ledger.' },
  { when: 'Two minutes', body: 'read-only access, and your outstanding list on screen.' },
  { when: 'Before anything sends', body: 'you read the four messages and change any word you like.' },
]

// See Home.tsx's matching comment for why these are exported.
export const seoTitle = 'Contact — Join the Waitlist or Talk to Us'
export const seoDescription =
  'Tell us which accounting system you use and roughly how many invoices a month you send. RemindLoop sets the account up with you.'

export default function Contact() {
  const [searchParams] = useSearchParams()
  // /contact?reason=enterprise -- linked from Pricing.tsx's Enterprise
  // "Contact sales" CTA (a Scale-tier org past the 2,000-account
  // ceiling, or anyone asking about it up front). Pre-fills the note so
  // whoever reads it doesn't have to guess why the form was opened;
  // still the same generic form/confirmation, see this file's own
  // documented "no lead-capture endpoint yet" limitation above.
  const isEnterpriseInquiry = searchParams.get('reason') === 'enterprise'
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [system, setSystem] = useState<AccountingSystem>('Xero')
  const [volume, setVolume] = useState('')
  const [note, setNote] = useState(
    isEnterpriseInquiry
      ? "Enterprise inquiry -- we're past (or approaching) 2,000 active customer accounts and want to talk about a custom plan and a dedicated WhatsApp number."
      : '',
  )
  const [error, setError] = useState<string | null>(null)

  const submitMutation = useMutation({
    mutationFn: () =>
      api.post('/api/v1/contact', {
        name,
        email,
        accounting_system: system,
        invoice_volume: volume || null,
        note: note || null,
        is_enterprise_inquiry: isEnterpriseInquiry,
      }),
    onSuccess: () => setError(null),
    onError: (err) => setError(formatApiError(err, 'Could not send your message')),
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    submitMutation.mutate()
  }

  return (
    <MarketingShell>
      <Seo title={seoTitle} description={seoDescription} path="/contact" />

      <section className="mx-auto max-w-6xl px-[clamp(18px,4vw,44px)] pt-[clamp(44px,7vw,88px)] pb-[clamp(24px,3vw,36px)]">
        <h1 className="max-w-[20ch] text-[clamp(30px,4.4vw,48px)] leading-[1.1] font-semibold tracking-[-0.03em]">
          {isEnterpriseInquiry ? 'Let’s talk Enterprise' : 'Join the waitlist, or ask us anything'}
        </h1>
        <p className="mt-4.5 max-w-[58ch] text-[clamp(16px,1.5vw,18px)] leading-[1.6] text-foreground/76">
          {isEnterpriseInquiry
            ? 'Custom pricing, a dedicated WhatsApp number, and a plan built around your actual volume. Tell us a bit about your setup below and we will get back to you.'
            : 'Tell us which accounting system you use and roughly how many invoices a month you send. We will set the account up with you, and nothing goes to a customer until you have approved the wording.'}
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-[clamp(18px,4vw,44px)] py-[clamp(14px,2vw,28px)] pb-[clamp(40px,6vw,72px)]">
        <div className="grid grid-cols-1 items-start gap-x-[clamp(28px,5vw,80px)] gap-y-10 sm:grid-cols-2">
          <form onSubmit={handleSubmit} className="flex max-w-[460px] flex-col gap-5">
            <Input
              id="contact-name"
              label="Your name"
              placeholder="Ada Nwosu"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input
              id="contact-email"
              type="email"
              label="Work email"
              placeholder="you@yourcompany.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div>
              <span className="mb-1 block text-sm font-medium text-foreground">Accounting system</span>
              <div className="flex flex-wrap gap-1 rounded-lg bg-muted p-1 text-sm">
                {SYSTEMS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setSystem(option)}
                    aria-pressed={system === option}
                    className={`flex-1 rounded-md px-3 py-1.5 font-medium whitespace-nowrap ${
                      system === option ? 'bg-card shadow-sm' : 'text-muted-foreground'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            <Input
              id="contact-volume"
              label="Invoices a month, roughly"
              placeholder="e.g. 80"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
            />
            <Input
              id="contact-note"
              label="Anything we should know"
              placeholder="Optional"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-3.5">
              <Button
                type="submit"
                isLoading={submitMutation.isPending}
                disabled={submitMutation.isSuccess}
                className="min-h-11 bg-magenta-700 text-white hover:bg-magenta-800"
              >
                {submitMutation.isSuccess ? 'Sent' : 'Send'}
              </Button>
              {submitMutation.isSuccess && (
                <span className="text-sm text-foreground/70">
                  Thanks — we will reply within a day about {system}.
                </span>
              )}
            </div>
            {error && <p className="text-sm text-danger-600">{error}</p>}
          </form>

          <div>
            <h2 className="text-[24px] leading-[1.2] font-semibold">What happens next</h2>
            <div className="mt-3.5 flex flex-col gap-3.5">
              {nextSteps.map((step) => (
                <p key={step.when} className="text-body text-foreground/78">
                  <span className="font-semibold text-brand-700">{step.when}</span> — {step.body}
                </p>
              ))}
            </div>

            <h2 className="mt-10 text-[24px] leading-[1.2] font-semibold">Prefer to talk?</h2>
            <p className="mt-2 text-body text-foreground/78">
              Ask for a fifteen-minute walkthrough and we will run the demo against your own kind
              of ledger. <a href="mailto:hello@getremindloop.com" className="text-brand-700 hover:underline">hello@getremindloop.com</a>
            </p>
          </div>
        </div>
      </section>
    </MarketingShell>
  )
}
